/**
 * サーバーイベントスキーマ
 *
 * WebSocket経由でサーバーからクライアントへ送信されるイベント
 */

import { z } from "zod";
import { BingoNumberSchema } from "./base";
import {
  GameEventLogSchema,
  GameSchema,
  HostSchema,
  ParticipantSchema,
  RoomSettingsSchema,
  WinningLineSchema,
} from "./room";

// ============================================
// 基本イベントスキーマ
// ============================================

/** 基本イベントプロパティ（roomId, timestamp, version） */
const BaseEventSchema = z.object({
  roomId: z.string().optional(),
  timestamp: z.string().optional(),
  version: z.number().optional(),
});

/** チャットメッセージスキーマ */
const ChatMessageSchema = z.object({
  id: z.string(),
  participantId: z.string(),
  nickname: z.string(),
  content: z.string(),
  timestamp: z.string(),
});

// ============================================
// 個別イベントスキーマ
// ============================================

/** SYNC イベントスキーマ */
const SyncEventSchema = z.object({
  type: z.literal("SYNC"),
  room: z.object({
    id: z.string(),
    code: z.string(),
    status: z.enum(["created", "waiting", "playing", "paused", "finished", "closed"]),
    host: HostSchema,
    participants: z.record(z.string(), ParticipantSchema),
    game: GameSchema.nullable(),
    settings: RoomSettingsSchema,
    createdAt: z.string(),
    expiresAt: z.string(),
  }),
  yourParticipantId: z.string(),
  yourCard: z
    .object({
      id: z.string(),
      grid: z.array(z.union([BingoNumberSchema, z.literal("FREE")])),
      markedIndices: z.array(z.number()),
      themeId: z.string(),
    })
    .optional(),
  eventLogs: z.array(GameEventLogSchema).optional(),
  chatMessages: z.array(ChatMessageSchema).optional(),
});

/** NUMBER_DRAWN イベントスキーマ */
const NumberDrawnEventSchema = BaseEventSchema.extend({
  type: z.literal("NUMBER_DRAWN"),
  number: BingoNumberSchema,
  drawnNumbers: z.array(BingoNumberSchema),
  drawnBy: z.enum(["host", "auto"]),
  remainingCount: z.number(),
});

/** CARD_MARKED イベントスキーマ */
const CardMarkedEventSchema = BaseEventSchema.extend({
  type: z.literal("CARD_MARKED"),
  participantId: z.string(),
  number: BingoNumberSchema,
  cellIndex: z.number(),
  markedIndices: z.array(z.number()),
});

/** BINGO_ACHIEVED イベントスキーマ */
const BingoAchievedEventSchema = BaseEventSchema.extend({
  type: z.literal("BINGO_ACHIEVED"),
  participantId: z.string(),
  nickname: z.string(),
  winningLines: z.array(WinningLineSchema),
  bingoCount: z.number(),
});

/** GAME_STARTED イベントスキーマ */
const GameStartedEventSchema = BaseEventSchema.extend({
  type: z.literal("GAME_STARTED"),
  startedAt: z.string(),
});

/** GAME_FINISHED イベントスキーマ */
const GameFinishedEventSchema = BaseEventSchema.extend({
  type: z.literal("GAME_FINISHED"),
  winners: z.array(z.string()),
  finishedAt: z.string(),
});

/** 簡易イベントスキーマ（共通フォーマット） */
const SimpleEventSchema = BaseEventSchema.extend({
  type: z.enum([
    "ROOM_CREATED",
    "ROOM_STATUS_CHANGED",
    "ROOM_SETTINGS_UPDATED",
    "ROOM_CLOSED",
    "PARTICIPANT_JOINED",
    "PARTICIPANT_LEFT",
    "PARTICIPANT_RECONNECTED",
    "GAME_PAUSED",
    "GAME_RESUMED",
    "SPIN_STARTED",
  ]),
}).passthrough();

/** ERROR イベントスキーマ */
const ErrorEventSchema = z.object({
  type: z.literal("ERROR"),
  code: z.string(),
  message: z.string(),
});

/** PONG イベントスキーマ */
const PongEventSchema = z.object({
  type: z.literal("PONG"),
  timestamp: z.string(),
});

/** CHAT_MESSAGE_RECEIVED イベントスキーマ */
const ChatMessageReceivedEventSchema = BaseEventSchema.extend({
  type: z.literal("CHAT_MESSAGE_RECEIVED"),
  message: ChatMessageSchema,
});

// ============================================
// 統合スキーマ
// ============================================

/** サーバーイベントスキーマ（統合） */
export const ServerEventSchema = z.union([
  SyncEventSchema,
  NumberDrawnEventSchema,
  CardMarkedEventSchema,
  BingoAchievedEventSchema,
  GameStartedEventSchema,
  GameFinishedEventSchema,
  ChatMessageReceivedEventSchema,
  SimpleEventSchema,
  ErrorEventSchema,
  PongEventSchema,
]);

/** サーバーイベント型（スキーマから推論） */
export type ServerEventFromSchema = z.infer<typeof ServerEventSchema>;
